import { AlertTriangle, Trash2 } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';

import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@/components/cargofl-ui';
import { ApiError } from '@/lib/api';
import type { BulkDeleteResult } from '@/lib/bulk-api';

/**
 * Delete several records at once.
 *
 * The real use is undoing a bad import - 200 assets from the wrong spreadsheet, none
 * of them ever issued. Without it the only remedy is 200 clicks or SQL.
 *
 * It changes NOTHING about what may be deleted. Anything with history is still
 * refused, one row at a time, and the result names exactly which ones were skipped and
 * why. A bulk action that destroyed more than the single action would is the worst
 * possible version of this.
 *
 * Confirmation is typed, not a checkbox. This is the only irreversible action in the
 * app that operates on many rows at once, and an accidental double-Enter on a button
 * should not be able to do it.
 */
export function BulkDeleteDialog({
  open,
  onOpenChange,
  count,
  noun,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  count: number;
  /** "asset" / "employee" - pluralised here so call sites do not each do it. */
  noun: string;
  onConfirm: () => Promise<BulkDeleteResult>;
}) {
  const [typed, setTyped] = React.useState('');
  const [result, setResult] = React.useState<BulkDeleteResult | null>(null);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setTyped('');
      setResult(null);
    }
  }, [open]);

  const plural = count === 1 ? noun : `${noun}s`;
  const confirmed = typed.trim().toUpperCase() === 'DELETE';

  const run = async () => {
    setBusy(true);
    try {
      const outcome = await onConfirm();
      setResult(outcome);

      if (outcome.deleted > 0 && outcome.skipped.length === 0) {
        toast.success(`${outcome.deleted} ${outcome.deleted === 1 ? noun : `${noun}s`} deleted.`);
        onOpenChange(false);
      } else if (outcome.deleted === 0) {
        toast.error('Nothing was deleted.');
      } else {
        // Kept open deliberately: the skipped list is the useful half of the answer,
        // and a toast that vanishes in four seconds is not where it belongs.
        toast.warning(`${outcome.deleted} deleted, ${outcome.skipped.length} kept.`);
      }
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'That could not be done.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Delete {count} {plural}?
          </DialogTitle>
          <DialogDescription>This cannot be undone.</DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-3">
            <Alert className={result.deleted > 0 ? 'border-success/30' : undefined}>
              <AlertDescription className="text-sm">
                <span className="font-medium">{result.deleted}</span> deleted,{' '}
                <span className="font-medium">{result.skipped.length}</span> kept.
              </AlertDescription>
            </Alert>

            {result.skipped.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium">
                  Kept, because deleting them would destroy history:
                </p>
                <ul className="max-h-56 space-y-1.5 overflow-auto rounded-md border p-2">
                  {result.skipped.map((skip) => (
                    <li key={skip.id} className="text-xs">
                      <span className="font-medium">{skip.label}</span>
                      <span className="block text-muted-foreground">{skip.reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <Alert className="border-destructive/30">
              <AlertDescription className="flex gap-2 text-xs leading-relaxed">
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-destructive" />
                <span>
                  Anything with history is kept automatically — an asset that has been issued or
                  repaired, a person who has held something or been exited. You will be told exactly
                  which, and why. Only records that have never been used are actually deleted.
                </span>
              </AlertDescription>
            </Alert>

            <div className="space-y-1.5">
              <Label htmlFor="confirm-delete">
                Type <span className="font-mono font-semibold">DELETE</span> to confirm
              </Label>
              <Input
                id="confirm-delete"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoComplete="off"
                placeholder="DELETE"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {result ? 'Close' : 'Cancel'}
          </Button>
          {!result && (
            <>
            <Button hint={"Delete them for good — anything with history is refused and listed"} variant="destructive" disabled={!confirmed || busy} onClick={() => void run()}>
              <Trash2 className="mr-1.5 size-4" />
              {busy ? 'Deleting…' : `Delete ${count} ${plural}`}
            </Button>
            {!confirmed && <span className="self-center text-xs text-muted-foreground">Tick the box above to confirm</span>}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The bar that appears once anything is ticked. */
export function SelectionBar({
  count,
  noun,
  onClear,
  onDelete,
  actions,
}: {
  count: number;
  noun: string;
  onClear: () => void;
  /** Optional: deleting is SUPER_ADMIN's alone, but the bar is worth having without it. */
  onDelete?: () => void;
  /** Anything else the selection can be acted on with, shown before Delete. */
  actions?: React.ReactNode;
}) {
  if (count === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/50 px-3 py-2">
      <span className="at-num text-sm font-medium">
        {count} {count === 1 ? noun : `${noun}s`} selected
      </span>
      <Button hint={"Clear the selection"} size="sm" variant="ghost" onClick={onClear}>
        Clear
      </Button>
      <div className="ml-auto flex flex-wrap items-center gap-2">
        {actions}
        {onDelete && (
          <Button hint={"Delete the selected rows — anything with history is refused"} size="sm" variant="destructive" onClick={onDelete}>
            <Trash2 className="mr-1.5 size-4" /> Delete selected
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Tracked selection across a paged table.
 *
 * A Set of ids rather than an index or a row reference, so the selection survives a
 * re-fetch, a filter change and a page change - and so "select all" on page 2 adds to
 * page 1 rather than replacing it.
 */
export function useSelection() {
  const [selected, setSelected] = React.useState<Set<string>>(new Set());

  const toggle = React.useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = React.useCallback((ids: string[], on: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }, []);

  const clear = React.useCallback(() => setSelected(new Set()), []);

  return { selected, toggle, toggleAll, clear, ids: [...selected] };
}

/**
 * Delete ONE record.
 *
 * Separate from the bulk dialog because the question is different. In bulk, "3 of
 * these 200 were kept" is a summary. For a single row, being refused is the entire
 * outcome, and it needs to say what to do instead - retire the asset, exit the person
 * - rather than flashing a toast that disappears before it is read.
 *
 * Administrator-only at the call site AND on the route. The rule about history is
 * enforced by the API; this only explains it before and after.
 */
export function DeleteRecordDialog({
  open,
  onOpenChange,
  label,
  noun,
  /** What to do instead when the record has history. */
  alternative,
  onDelete,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** "AST-1004 — Dell Latitude 5440" */
  label: string;
  noun: string;
  alternative: string;
  onDelete: () => Promise<unknown>;
}) {
  const [busy, setBusy] = React.useState(false);
  const [refusal, setRefusal] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) setRefusal(null);
  }, [open]);

  const run = async () => {
    setBusy(true);
    setRefusal(null);
    try {
      await onDelete();
      toast.success(`${label} deleted.`);
      onOpenChange(false);
    } catch (error) {
      // Kept in the dialog rather than only in a toast: this is the whole answer, and
      // it names the thing to do instead.
      const message = error instanceof ApiError ? error.message : 'That could not be deleted.';
      setRefusal(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete this {noun}?</DialogTitle>
          <DialogDescription>{label}</DialogDescription>
        </DialogHeader>

        {refusal ? (
          <Alert className="border-destructive/30">
            <AlertDescription className="flex gap-2 text-sm leading-relaxed">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" />
              <span>{refusal}</span>
            </AlertDescription>
          </Alert>
        ) : (
          <Alert>
            <AlertDescription className="text-xs leading-relaxed">
              This cannot be undone. It only works if this {noun} has never been used — if it has
              history, {alternative}
            </AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {refusal ? 'Close' : 'Cancel'}
          </Button>
          {!refusal && (
            <Button variant="destructive" disabled={busy} onClick={() => void run()}>
              <Trash2 className="mr-1.5 size-4" />
              {busy ? 'Deleting…' : 'Delete'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
